import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CheckIcon from '@mui/icons-material/Check';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from '@mui/material/styles';
import { ReactComponent as LeftBackground } from '../../../icons/promotion-banner/left-background.svg';
import { ReactComponent as Hourglass } from '../../../icons/promotion-banner/hourglass.svg';
import { ReactComponent as HolidayLeftBackground } from '../../../icons/promotion-banner/holiday-offer/left-background.svg';
import { ReactComponent as HolidayHourglass } from '../../../icons/promotion-banner/holiday-offer/hourglass.svg';
import { ReactComponent as NewYear2025LeftBg } from '../../../icons/promotion-banner/newyear2025-offer/left-background.svg';
import { ReactComponent as NewYear2025Hourglass } from '../../../icons/promotion-banner/newyear2025-offer/hourglass.svg';
import {
  PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH,
  PROMOTION_BANNER_SHOW_DESC_WIDTH,
} from '../../utils';
import {
  PromoBannerComponentName,
  PromotionCampaignPrefix,
  PromotionCampaignProps,
  PromotionPriceProps,
} from '../../../../models';

const useStyles = makeStyles((theme => ({
  rootContainer: {
    position: 'relative',
  },
  limitedOfferContainer: {
    display: 'inline-grid',
    width: theme.spacing(55),
  },
  countdownContainer: {
    position: 'absolute',
    justifyContent: 'center',
    left: theme.spacing(60),
    top: theme.spacing(0),
    right: theme.spacing(40),
  },
  countdownMinSize: {
    right: theme.spacing(0),
  },
  descriptionContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(4),
  },
  countdownTextContainer: {
    display: 'flex',
    height: theme.spacing(12),
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
    fontSize: theme.spacing(3),
    lineHeight: theme.spacing(3),
  },
  ellipseBackground: {
    height: theme.spacing(12),
    position: 'absolute',
    borderRadius: theme.spacing(0.75, 0, 0, 0),
    bottom: theme.spacing(0),
    left: theme.spacing(0),
  },
  originalPrice: {
    position: 'absolute',
    top: theme.spacing(1.25),
    left: theme.spacing(35.6),
    lineHeight: theme.spacing(2),
    fontSize: theme.spacing(2),
    fontWeight: 400,
    textDecoration: 'line-through',
    color: '#000000',
  },
  discountPrice: {
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(31.25),
    lineHeight: theme.spacing(6),
    fontSize: theme.spacing(4.5),
    fontWeight: 700,
    color: '#5959EB',
  },
  unitPrice: {
    position: 'absolute',
    top: theme.spacing(8.75),
    left: theme.spacing(34),
    lineHeight: theme.spacing(2),
    fontSize: theme.spacing(2),
    fontWeight: 400,
    color: '#000000',
  },
  countdownText: {
    width: 'auto',
  },
  hourglass: {
    marginRight: theme.spacing(1),
    minWidth: theme.spacing(3.5),
    width: theme.spacing(3.5),
    minHeight: theme.spacing(12),
    height: theme.spacing(12),
    lineHeight: theme.spacing(2),
    transform: 'rotate(20deg)',
  },
  checkIcon: {
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  descriptionText: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 400,
    fontSize: theme.spacing(1.5),
    lineHeight: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
})));

type StyledDivProps = {
  bannerStyle?: any,
};

const StyledDiv = styled('div')<StyledDivProps>(({ theme, bannerStyle }) => ({
  width: bannerStyle?.width ?? '100%',
  height: bannerStyle?.height ?? theme.spacing(12),
  borderRadius: theme.spacing(1, 0.75, 0, 0),
  backgroundColor: bannerStyle?.backgroundColor ?? '#5959EB',
}));

enum CondownFormat {
  LONG = 'long',
  SHORT = 'short',
}

const DEFAULT_PRICE_CONFIG = {
  currentPrice: 2099,
  discountPrice: 1000,
  priceUnit: 'per license',
};

const bannerAssets: Record<PromotionCampaignPrefix, Map<PromoBannerComponentName, JSX.Element>> = {
  [PromotionCampaignPrefix.DEFAULT]: new Map([
    [PromoBannerComponentName.LEFT_BACKGROUND, <LeftBackground />],
    [PromoBannerComponentName.HOUR_GLASS, <Hourglass />],
  ]),
  [PromotionCampaignPrefix.HOLIDAYPROMO]: new Map([
    [PromoBannerComponentName.LEFT_BACKGROUND, <HolidayLeftBackground />],
    [PromoBannerComponentName.HOUR_GLASS, <HolidayHourglass />],
  ]),
  [PromotionCampaignPrefix.NEWYEAR2025]: new Map([
    [PromoBannerComponentName.LEFT_BACKGROUND, <NewYear2025LeftBg />],
    [PromoBannerComponentName.HOUR_GLASS, <NewYear2025Hourglass />],
  ]),
};

const PromotionPrice = (props: PromotionPriceProps) => {
  const classes = useStyles();
  const intl = useIntl();
  const {
    currentPrice,
    discountPrice,
    priceUnit,
  } = props;

  return (
    <>
      <Typography variant="h4" color="white" className={classes.originalPrice}>
        <FormattedMessage
          id="promotion.campaign.banner.price"
          values={{ price: intl.formatNumber(currentPrice ?? DEFAULT_PRICE_CONFIG.currentPrice) }}
        />
      </Typography>
      <Typography variant="h4" color="white" className={classes.discountPrice}>
        <FormattedMessage
          id="promotion.campaign.banner.price"
          values={{ price: intl.formatNumber(discountPrice ?? DEFAULT_PRICE_CONFIG.discountPrice) }}
        />
      </Typography>
      <Typography variant="h4" color="white" className={classes.unitPrice}>
        <FormattedMessage
          id="promotion.campaign.banner.price.unit"
          values={{ unit: priceUnit ?? DEFAULT_PRICE_CONFIG.priceUnit }}
        />
      </Typography>
    </>
  );
};

const PromotionCampaignBanner = (props: PromotionCampaignProps) => {
  const classes = useStyles();
  const [showDescription, setShowDescription] = useState(true);
  const [countdownFormat, setCountdownFormat] = useState<string>(CondownFormat.LONG);

  const bannerRef = useCallback(node => {
    if (node !== null) {
      const resizeObserver = new ResizeObserver(() => {
        setShowDescription(node.clientWidth > PROMOTION_BANNER_SHOW_DESC_WIDTH);
        setCountdownFormat(
          node.clientWidth > PROMOTION_BANNER_COUNTDOWN_FORMAT_WIDTH
            ? CondownFormat.LONG
            : CondownFormat.SHORT,
        );
      });
      resizeObserver.observe(node);
    }
  }, []);

  const intl = useIntl();
  const {
    endDate,
    priceConfig,
    bannerConfig,
  } = props;

  const [countdownText, setCountdownText] = useState('');

  const assets = bannerConfig
    ? bannerAssets[bannerConfig.promotionPrefix]
    : bannerAssets[PromotionCampaignPrefix.DEFAULT];

  dayjs.extend(utc);
  dayjs.extend(timezone);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = dayjs().utc().format();

      const dayDiff = dayjs(endDate).diff(Date.now(), 'd');
      const hourDiff = dayjs(endDate).diff(now, 'h');
      const minDiff = dayjs(endDate).diff(now, 'm');
      const secondDiff = dayjs(endDate).diff(now, 's');

      if (dayDiff <= 0) {
        if (secondDiff <= 0) {
          clearInterval(interval);
        } else {
          setCountdownText(intl.formatMessage(
            { id: `promotion.campaign.countdown.second.${countdownFormat}` },
            {
              h: hourDiff % 60,
              m: minDiff % 60 % 60,
              s: secondDiff % 60,
            },
          ));
        }
      } else {
        setCountdownText(intl.formatMessage(
          { id: `promotion.campaign.countdown.min.${countdownFormat}` },
          {
            d: dayDiff,
            h: hourDiff % 60 % 24,
            m: minDiff % 60,
          },
        ));
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [endDate, countdownFormat]);

  const renderLimitedOffer = () => (
    <>
      {assets.get(PromoBannerComponentName.LEFT_BACKGROUND)}
      {!bannerConfig?.hidePromotionPrice
        && (
        <PromotionPrice
          discountPrice={priceConfig?.discountPrice}
          currentPrice={priceConfig?.currentPrice}
          priceUnit={priceConfig?.priceUnit}
        />
        )}
    </>
  );

  const renderCountDownComponent = () => (
    <>
      <Typography variant="h4" color="white" className={classes.countdownTextContainer}>
        <span className={classes.countdownText}>
          {countdownText}
        </span>
        {assets.get(PromoBannerComponentName.HOUR_GLASS)}
      </Typography>
    </>
  );

  const renderDescriptionComponent = () => (
    <>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.first.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.second.description" />
      </Typography>
      <Typography variant="body1" color="white" className={classes.descriptionText}>
        <CheckIcon className={classes.checkIcon} />
        <FormattedMessage id="promotion.campaign.banner.third.description" />
      </Typography>
    </>
  );

  return (
    <div ref={bannerRef} id="promotion-campaign-banner" className={classes.rootContainer}>
      <StyledDiv bannerStyle={bannerConfig?.bannerStyle}>
        <div className={classes.limitedOfferContainer}>
          {renderLimitedOffer()}
        </div>
        <div className={showDescription ? classes.countdownContainer : `${classes.countdownContainer} ${classes.countdownMinSize}`}>
          {renderCountDownComponent()}
        </div>
        {
          showDescription && (
            <div className={classes.descriptionContainer}>
              {renderDescriptionComponent()}
            </div>
          )
        }
      </StyledDiv>
    </div>
  );
};

export default PromotionCampaignBanner;
